import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const BodyFatPercentage = () => {
  // State variables for Body Fat Percentage calculation
  const [bmi, setBmi] = useState(0); // Body Mass Index
  const [age, setAge] = useState(0); // Age
  const [gender, setGender] = useState(0); // Gender (1 for male, 0 for female)
  const [bodyFatPercentage, setBodyFatPercentage] = useState(0); // Calculated body fat percentage

  // Event handlers to update state values
  const handleBmiChange = (e) => setBmi(parseFloat(e.target.value) || 0);
  const handleAgeChange = (e) => setAge(parseInt(e.target.value) || 0);
  const handleGenderChange = (e) => setGender(parseInt(e.target.value) || 0);

  // useEffect hook to calculate body fat percentage when any input changes
  useEffect(() => {
    if (bmi && age && gender >= 0) {
      const percentage = 1.2 * bmi + 0.23 * age - 10.8 * gender - 5.4;
      setBodyFatPercentage(percentage);
    } else {
      setBodyFatPercentage(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [bmi, age, gender]);

  return (
    <Layout title="Body Fat Percentage Calculator" path="body-fat-percentage">
      <section className="section-padding" id="body-fat-percentage-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Body Fat Percentage Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Body Fat Percentage Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* BMI Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="bmi" className="form-label">
                        BMI (Body Mass Index)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>BMI:</strong> Enter your Body Mass Index (BMI), which is calculated based on your weight and height."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="bmi"
                        placeholder="Enter your BMI"
                        value={bmi || ""}
                        onChange={handleBmiChange}
                      />
                    </div>

                    {/* Age Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="age" className="form-label">
                        Age
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Age:</strong> Enter your age in years to improve the calculation accuracy."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="age"
                        placeholder="Enter your age"
                        value={age || ""}
                        onChange={handleAgeChange}
                      />
                    </div>

                    {/* Gender Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="gender" className="form-label">
                        Gender
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Gender:</strong> Select your gender as it influences the body fat percentage calculation."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        className="form-control"
                        id="gender"
                        value={gender || ""}
                        onChange={handleGenderChange}
                      >
                        <option value="0">Female</option>
                        <option value="1">Male</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Body Fat Percentage:{" "}
                        <span className="fw-bold ms-3">
                          {bodyFatPercentage === 0
                            ? "0.00"
                            : bodyFatPercentage.toFixed(2)}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Body Fat Percentage Calculator
                </h3>

                {/* What is a Body Fat Percentage Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Body Fat Percentage Calculator?
                </h4>
                <p>
                  A **Body Fat Percentage Calculator** estimates your body fat
                  percentage by considering your BMI, age, and gender. This
                  provides a more accurate understanding of your body
                  composition compared to BMI alone.
                </p>

                {/* Why Use a Body Fat Percentage Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Body Fat Percentage Calculator?
                </h4>
                <p>
                  Unlike BMI, which only considers height and weight, body fat
                  percentage accounts for fat versus lean mass. This makes it a
                  better measure of fitness, health, and weight management,
                  providing you with a clearer picture of your overall health.
                </p>

                {/* Benefits of Using a Body Fat Percentage Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Body Fat Percentage Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps individuals monitor health and fitness levels:
                    </strong>{" "}
                    Provides a more accurate and comprehensive assessment of
                    health than BMI alone.
                  </li>
                  <li>
                    <strong>Useful for weight management programs:</strong>{" "}
                    Helps track fat loss and muscle gain over time, improving
                    weight management strategies.
                  </li>
                  <li>
                    <strong>
                      Provides more accurate health assessment than BMI:
                    </strong>{" "}
                    Body fat percentage reflects fat distribution in the body,
                    giving better insights into health risks.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Body Fat Percentage Calculator?
                </h4>
                <p>The Body Fat Percentage Calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Fitness Centers:</strong> To track client progress
                    in health and fitness goals.
                  </li>
                  <li>
                    <strong>Personal Trainers:</strong> To assess clients' body
                    composition and tailor fitness programs accordingly.
                  </li>
                  <li>
                    <strong>Health Apps:</strong> To give users insights into
                    their body fat percentage and overall health status.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is a healthy body fat percentage?
                </h4>
                <p>
                  A healthy body fat percentage for men typically ranges from 6%
                  to 24%, while for women, it ranges from 16% to 30%. The ideal
                  range can vary based on age, fitness level, and individual
                  health conditions.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is body fat percentage more accurate than BMI?
                </h4>
                <p>
                  BMI does not distinguish between fat and lean body mass,
                  meaning a person with high muscle mass might be classified as
                  overweight despite having low body fat. Body fat percentage,
                  however, directly measures fat levels and provides a better
                  indicator of health and fitness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BodyFatPercentage;
